import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { asArray } from "@/lib/asArray";
import { fmtDateTimeFirm, fmtDate } from "@/lib/dt";   // 5c · F-5
import React, { useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import FullScreenDialog from "@/components/FullScreenDialog";
import {
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeftRight, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)
import { fmtNumber, fmtCount } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter

/**
 * Per-head timeline modal — lists every head-wise upload that touched the
 * given `fsCode` (append + replace) with a "Preview diff" link that shows
 * the snapshot-vs-current side-by-side.
 */
export default function HeadTimelineDialog({ open, onOpenChange, engagementId,
                                              fsCode, onUndone }) {
  const colW = useResizableColumns({ storageKey: "sampling-head-timeline", defaults: [130, 160, 160, 130, 130, 110, 130], mins: [96, 100, 100, 90, 90, 72, 90] });   // Step 11d · add-on
  const { t } = useI18n();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [diffOpen, setDiffOpen] = useState(null);   // upload_id
  const [diffData, setDiffData] = useState(null);
  const [undoing, setUndoing] = useState(null);

  useEffect(() => {
    if (!open || !engagementId) return;
    setLoading(true);
    api.get(`/gl/${engagementId}/head-uploads`, { _silent: true })
      .then((r) => setUploads(asArray(r.data).filter((u) =>
        (u.fs_codes || []).includes(fsCode))))
      .catch(() => setUploads([]))
      .finally(() => setLoading(false));
  }, [open, engagementId, fsCode]);

  const openDiff = async (uploadId) => {
    setDiffOpen(uploadId); setDiffData(null);
    try {
      const r = await api.get(
        `/gl/${engagementId}/head-uploads/${uploadId}/diff`, { _silent: true });
      setDiffData(r.data);
    } catch (e) { toast.error(toErrMsg(e, t("headTimeline.diffFailed"))); }
  };

  const undoUpload = async (uploadId) => {
    if (!await askConfirm(t("headTimeline.undoConfirm"))) return;
    setUndoing(uploadId);
    try {
      const r = await api.post(
        `/gl/${engagementId}/head-uploads/${uploadId}/undo`);
      toast.success(t("headTimeline.undoComplete").replace("{deleted}", r.data.deleted).replace("{restored}", r.data.restored));
      // Refresh
      const list = await api.get(`/gl/${engagementId}/head-uploads`,
        { _silent: true });
      setUploads(asArray(list.data).filter((u) => (u.fs_codes || []).includes(fsCode)));
      onUndone?.();
    } catch (e) { toast.error(toErrMsg(e, t("headTimeline.undoFailed"))); }
    finally { setUndoing(null); }
  };

  return (
    <FullScreenDialog open={open} onOpenChange={onOpenChange}
                      testid="head-timeline-dialog"
                      title={<>{t("headTimeline.title")} — {fsCode}</>}>
        <DialogDescription className="mb-3">
            {t("headTimeline.desc")}
        </DialogDescription>

        {loading && (
          <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" /> {t("headTimeline.loading")}
          </div>
        )}
        {!loading && uploads.length === 0 && (
          <div className="p-4 text-center text-sm text-muted-foreground">
            {t("headTimeline.noUploads").replace("{code}", fsCode)}
          </div>
        )}
        {!loading && uploads.length > 0 && (
          <div className="rounded-md border bg-white dark:bg-slate-950"
               data-testid="head-timeline-list">
            <ResizableTable cols={colW} wrap className="w-full text-xs">
              <thead className="border-b bg-slate-50 text-[10px] uppercase text-muted-foreground">
                <tr>
                  <ResizableTh index={0} className="px-3 py-1.5 text-start">{t("headTimeline.when")}</ResizableTh>
                  <ResizableTh index={1} className="px-3 py-1.5 text-start">{t("headTimeline.by")}</ResizableTh>
                  <ResizableTh index={2} className="px-3 py-1.5 text-start">{t("headTimeline.mode")}</ResizableTh>
                  <ResizableTh index={3} className="px-3 py-1.5 text-end">{t("headTimeline.inserted")}</ResizableTh>
                  <ResizableTh index={4} className="px-3 py-1.5 text-end">{t("headTimeline.replaced")}</ResizableTh>
                  <ResizableTh index={5} className="px-3 py-1.5 text-start">{t("headTimeline.status")}</ResizableTh>
                  <ResizableTh index={6} className="px-3 py-1.5 text-end">{t("headTimeline.actions")}</ResizableTh>
                </tr>
              </thead>
              <tbody>
                {uploads.map((u) => (
                  <tr key={u.id} className="border-b last:border-b-0"
                      data-testid={`timeline-row-${u.id}`}>
                    <td className="px-3 py-1">
                      {fmtDateTimeFirm(u.uploaded_at)}
                    </td>
                    <td className="px-3 py-1">{u.uploaded_by_name || "—"}</td>
                    <td className="px-3 py-1">
                      <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase ${
                        u.mode === "replace"
                          ? "bg-amber-100 text-amber-800"
                          : "bg-emerald-100 text-emerald-800"
                      }`}>{u.mode}</span>
                    </td>
                    <td className="px-3 py-1 num-cell">{fmtCount(u.inserted || 0)}</td>
                    <td className="px-3 py-1 num-cell">{fmtCount(u.replaced_count || 0)}</td>
                    <td className="px-3 py-1">
                      {u.undone_at ? (
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                              title={t("headTimeline.undoneBy").replace("{name}", u.undone_by_name || "—").replace("{at}", fmtDateTimeFirm(u.undone_at))}>
                          {t("headTimeline.undone")}
                        </span>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">{t("headTimeline.live")}</span>
                      )}
                    </td>
                    <td className="px-3 py-1 text-end">
                      {u.mode === "replace" && (
                        <Button size="sm" variant="outline" className="me-1"
                                onClick={() => openDiff(u.id)}
                                data-testid={`timeline-diff-${u.id}`}>
                          <ArrowLeftRight className="me-1 h-3 w-3" /> {t("headTimeline.diff")}
                        </Button>
                      )}
                      {u.mode === "replace" && !u.undone_at && (
                        <Button size="sm" variant="outline"
                                className="border-amber-400 text-amber-800 hover:bg-amber-50"
                                disabled={undoing === u.id}
                                onClick={() => undoUpload(u.id)}
                                data-testid={`timeline-undo-${u.id}`}>
                          {undoing === u.id
                            ? <Loader2 className="me-1 h-3 w-3 animate-spin" />
                            : <Undo2 className="me-1 h-3 w-3" />}
                          {t("headTimeline.undo")}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </ResizableTable>
          </div>
        )}

        {/* Diff sub-modal */}
        <FullScreenDialog open={!!diffOpen}
                           onOpenChange={(v) => { if (!v) { setDiffOpen(null); setDiffData(null); } }}
                           testid="head-diff-dialog"
                           title={t("headTimeline.diffTitle")}>
            <DialogDescription className="mb-3">
                {t("headTimeline.diffDesc")}
            </DialogDescription>
            {!diffData && (
              <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" /> {t("headTimeline.loadingDiff")}
              </div>
            )}
            {diffData && (
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded border">
                  <div className="border-b bg-slate-50 px-2 py-1 text-[10px] uppercase text-muted-foreground">
                    {t("headTimeline.snapshotHdr").replace("{n}", diffData.snapshot.length)}
                  </div>
                  <DiffTable rows={diffData.snapshot} testid="diff-snapshot" t={t} />
                </div>
                <div className="rounded border">
                  <div className="border-b bg-slate-50 px-2 py-1 text-[10px] uppercase text-muted-foreground">
                    {t("headTimeline.currentHdr").replace("{n}", diffData.current.length)}
                  </div>
                  <DiffTable rows={diffData.current} testid="diff-current" t={t} />
                </div>
              </div>
            )}
        </FullScreenDialog>
    </FullScreenDialog>
  );
}


function DiffTable({ rows, testid, t }) {   // Step 11d · add-on: named as a component (was _DiffTable) so it can hold its column widths
  const colW2 = useResizableColumns({ storageKey: "sampling-head-timeline-diff", defaults: [110, 160, 130, 130, 130], mins: [72, 100, 96, 90, 90] });   // Step 11d · add-on
  return (
    <div className="max-h-[55vh] overflow-y-auto" data-testid={testid}>
      <ResizableTable cols={colW2} className="w-full text-[10px]">
        <thead className="sticky top-0 bg-white dark:bg-slate-950">
          <tr className="border-b">
            <ResizableTh index={0} className="px-1 py-0.5 text-start">{t("headTimeline.voucher")}</ResizableTh>
            <ResizableTh index={1} className="px-1 py-0.5 text-start">{t("headTimeline.acc")}</ResizableTh>
            <ResizableTh index={2} className="px-1 py-0.5 text-start">{t("common.date")}</ResizableTh>
            <ResizableTh index={3} className="px-1 py-0.5 text-end">{t("common.debit")}</ResizableTh>
            <ResizableTh index={4} className="px-1 py-0.5 text-end">{t("common.credit")}</ResizableTh>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 500).map((r, i) => (
            <tr key={i} className="border-b last:border-b-0">
              <td className="px-1 py-0.5 font-mono">{r.voucher_no}</td>
              <td className="px-1 py-0.5">{r.account_code}</td>
              <td className="px-1 py-0.5">{fmtDate(r.entry_date)}</td>
              <td className="px-1 py-0.5 text-end num-cell">{fmtNumber(Number(r.debit || 0))}</td>
              <td className="px-1 py-0.5 text-end num-cell">{fmtNumber(Number(r.credit || 0))}</td>
            </tr>
          ))}
          {rows.length > 500 && (
            <tr>
              <td colSpan="5" className="px-1 py-1 text-center text-muted-foreground">
                {t("headTimeline.andMore").replace("{n}", rows.length - 500)}
              </td>
            </tr>
          )}
        </tbody>
      </ResizableTable>
    </div>
  );
}
